import { useCallback } from 'react';

import {
  CreatePaymentMethodCardData, StripeCardNumberElement, PaymentMethod, PaymentIntent
} from '@stripe/stripe-js';
import { useStripe, useElements, CardNumberElement } from '@stripe/react-stripe-js';

import { useAppSelector } from '@/store/hooks';
import { logger } from '@/services/logger/logger';
import { UserDetailsState } from '@/features/userDetails/userDetailsSlice';
import { StripeExecuteResponse } from '@/api/payments/stripeExecute';

const buildCreatePaymentMethodPayload = (
  userDetailsState: UserDetailsState,
  cardElement: StripeCardNumberElement,
): CreatePaymentMethodCardData => ({
  type: 'card',
  card: cardElement,
  billing_details: {
    name: `${userDetailsState.firstName} ${userDetailsState.lastName}`,
    email: userDetailsState.email,
    address: {
      line1: userDetailsState.address1,
      line2: userDetailsState.address2,
      city: userDetailsState.town,
      country: userDetailsState.country,
      postal_code: userDetailsState.postcode,
    },
  },
});

export function useStripePayment() {
  const userDetailsState = useAppSelector(state => state.userDetails);

  const stripe = useStripe();
  const elements = useElements();

  const triggerPayment = useCallback(async (
    callbacks?: {
      onSuccess?: (paymentMethod: PaymentMethod) => void,
      onError?: (error?: unknown) => void,
    }
  ) => {
    if (!stripe || !elements) {
      logger.error('Error initialising Stripe or Elements');
      if (callbacks?.onError) {
        callbacks.onError(new Error('Error initialising Stripe or Elements'));
      }
      return;
    }

    let cardElement;
    try {
      cardElement = elements.getElement(CardNumberElement);
    } catch (error) {
      logger.error('Error getting Stripe element');
      logger.error(error);
      if (callbacks?.onError) {
        callbacks.onError(error);
      }
    }

    if (!cardElement) {
      logger.error('Stripe card element error');
      if (callbacks?.onError) {
        callbacks.onError(new Error('Stripe card element error'));
      }
      return;
    }

    // API Reference: https://stripe.com/docs/js/payment_methods/create_payment_method
    let stripePaymentMethod;
    try {
      const payload = buildCreatePaymentMethodPayload(userDetailsState, cardElement);
      stripePaymentMethod = await stripe.createPaymentMethod(payload);
    } catch (error) {
      logger.error('Error creating Stripe payment method');
      logger.error(error);
      if (callbacks?.onError) {
        callbacks.onError(error);
      }
    }

    if (!stripePaymentMethod?.paymentMethod) {
      logger.error('Stripe payment method error');
      if (callbacks?.onError) {
        callbacks.onError(new Error('Stripe payment method error'));
      }
      return;
    }

    if (callbacks?.onSuccess) {
      callbacks.onSuccess(stripePaymentMethod.paymentMethod);
    }
  }, [userDetailsState, stripe, elements]);

  // See: https://stripe.com/docs/payments/3d-secure/authentication-flow?locale=en-GB
  // 3DS/SCA Triggering Test Card: 4000002500003155
  // This is usually triggered with 3DS / SCA actions prompts need to be responded to by the user
  // If Stripe execute has "requires_action" we must handle this with payment intents
  const run3dsAction = useCallback(async (data: StripeExecuteResponse, callbacks?: {
    onSuccess?: (paymentIntent: PaymentIntent) => void,
    onError?: (error?: unknown) => void,
  }) => {
    if (!stripe) {
      logger.error('Error initialising Stripe');
      if (callbacks?.onError) {
        callbacks.onError(new Error('Error initialising Stripe'));
      }
      return;
    }

    if (!data?.data?.payment_intent_client_secret) {
      logger.error('Error retrieving Stripe intent client secret');
      if (callbacks?.onError) {
        callbacks.onError(new Error('Error retrieving Stripe intent client secret'));
      }
      return;
    }

    const clientSecret = data.data.payment_intent_client_secret;

    let nextAction;
    try {
      nextAction = await stripe.handleNextAction({ clientSecret });
    } catch (error) {
      logger.error('Error processing Stripe next action');
      if (callbacks?.onError) {
        callbacks.onError(new Error('Error processing Stripe next action'));
      }
      return;
    }

    if (nextAction?.error?.code) {
      logger.error('Stripe 3DS/SCA failure');
      logger.error(nextAction.error);
      if (nextAction.error.code === 'payment_intent_authentication_failure') {
        // Handle 3DS/SCA authentication failure
        if (callbacks?.onError) {
          callbacks.onError(new Error('We are unable to authenticate your card'));
        }
        return;
      }
      // Handle general failure
      if (callbacks?.onError) {
        callbacks.onError(new Error('Something went wrong. Please try again.'));
      }
      return;
    }

    // A payment intent ID is required
    if (!nextAction?.paymentIntent?.id) {
      logger.error('Error retrieving Stripe next action payment intent');
      if (callbacks?.onError) {
        callbacks.onError(new Error('Something went wrong. Please try again.'));
      }
      return;
    }

    if (callbacks?.onSuccess) {
      callbacks.onSuccess(nextAction.paymentIntent);
    }
  }, [stripe]);

  return {
    triggerPayment,
    run3dsAction,
  };
}
